import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { BackendAllErrorCodes } from '@freelancer/datastore';
import { ResponseData } from '@freelancer/freelancer-http';
import type { LoginError } from '@freelancer/login-signup';
import { LogoSize } from '@freelancer/ui/logo';
import type { FormGroupTypeOf } from '@freelancer/utils';

@Component({
  standalone: false,
  selector: 'app-signup-gdpr-form',
  template: `
    <app-form-logo
      [showBackButton]="true"
      [size]="logoSize"
      [backTrackingLabel]="'SignupGdprForm-Back'"
      (back)="back.emit()"
    ></app-form-logo>
    <form
      class="Form"
      [formGroup]="formGroup"
      (ngSubmit)="handleComplete()"
    >
      <h1
        class="text-mid Form-heading mb-xxsmall"
        i18n="Signup GDPR email opt-in heading"
      >
        Email Opt-In
      </h1>
      <p
        class="text-left mb-small"
        i18n="Signup GDPR email opt-in description"
      >
        Would you like to receive marketing promotions, special offers,
        inspirations, and updates to Freelancer's products and policies? You can
        unsubscribe at any time.
      </p>
      @if (formGroup.controls.privacyConsent; as control) {
        <div class="mb-large">
          <fl-checkbox
            flTrackingLabel="SignupGdprForm-PrivacyConsentCheckbox"
            i18n-label="Signup GDPR email opt-in label"
            label="Yes, I want to receive emails."
            [control]="control"
            [id]="'PrivacyConsent'"
          ></fl-checkbox>
        </div>
      }
      <h1
        class="text-mid Heading mb-xxsmall"
        i18n="Signup GDPR personal use heading"
      >
        Account Type
      </h1>
      <p
        class="text-left mb-small"
        i18n="Signup GDPR personal use description"
      >
        Do you intend to use Freelancer for personal use? Using Freelancer for
        cases other than business may be subject to additional VAT.
      </p>
      @if (formGroup.controls.personalUse; as control) {
        <div class="mb-large">
          <fl-checkbox
            flTrackingLabel="SignupGdprForm-PersonalUseCheckbox"
            i18n-label="Signup GDPR personal use label"
            label="Yes, I am using Freelancer for personal use."
            [control]="control"
            [id]="'PersonalUse'"
          ></fl-checkbox>
        </div>
      }
      @if (response && response.status === 'error') {
        <app-login-signup-errors
          class="mb-small"
          [response]="response"
          (switchToLogin)="switchToLogin.emit()"
        ></app-login-signup-errors>
      }
      <app-login-signup-button
        i18n="Signup GDPR signup button"
        [busy]="busy"
        [stepLabel]="'SignupGdprForm'"
      >
        Sign Up
      </app-login-signup-button>
    </form>
  `,
  styleUrls: ['./gdpr-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GdprFormComponent {
  @Input() logoSize = LogoSize.MID;
  @Input() formGroup: FormGroup<
    FormGroupTypeOf<{ privacyConsent: boolean; personalUse: boolean }>
  >;
  @Input() response?: ResponseData<unknown, BackendAllErrorCodes | LoginError>;
  @Input() busy = false;

  @Output() back = new EventEmitter();
  @Output() complete = new EventEmitter();
  @Output() switchToLogin = new EventEmitter();

  handleComplete(): void {
    if (this.response === null) {
      return;
    }
    this.complete.emit();
  }
}
